import { Formik, Form } from "formik";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Divider,
  Grid,
  Header,
  Segment,
  Label,
} from "semantic-ui-react";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useMediaQuery } from "react-responsive";
import {
  addEmailTemplateToDb,
  updateEmailTemplateInDb,
} from "../../../app/firestore/firestoreService";
import { closeModal } from "../../../app/common/modals/modalSlice";
import ModalWrapper from "../../../app/common/modals/modalWrapper";
import MyTextInput from "../../../app/common/form/MyTextInput";
import MyTextArea from "../../../app/common/form/MyTextArea";
import MyCheckbox from "../../../app/common/form/MyCheckbox";
import MySelectInput from "../../../app/common/form/MySelectInput";

const defaultEmailTemplateTypeOptions = [
  { value: "", text: "Not a default template" },
  { value: "sharing", text: "Default when sharing this form" },
  {
    value: "signing",
    text: "Default when sending this form for signatures",
  },
];

const defaultRecipientRoleOptions = [
  { value: "Buyers", text: "Buyers" },
  { value: "Sellers", text: "Sellers" },
  { value: "Listing Agent", text: "Listing Agent" },
  { value: "Buyer Agent", text: "Buyer Agent" },
  { value: "Buyer Lender", text: "Buyer Lender" },
  { value: "Escrow Officer", text: "Escrow Officer" },
  { value: "Title Company", text: "Title Company" },
];

function getDefaultEmailTemplateType(emailTemplate) {
  if (emailTemplate?.defaultEmailTemplateType) {
    return emailTemplate.defaultEmailTemplateType;
  }
  if (emailTemplate?.defaultForSigning) return "signing";
  if (emailTemplate?.defaultForSharing) return "sharing";
  return "";
}

function getDefaultRecipientRoles(emailTemplate, defaultEmailTemplateType) {
  if (Array.isArray(emailTemplate?.defaultRecipientRoles)) {
    return emailTemplate.defaultRecipientRoles;
  }
  if (defaultEmailTemplateType) {
    return defaultRecipientRoleOptions.map((option) => option.value);
  }
  return [];
}

export default function EmailTemplatesEdit({ emailTemplate }) {
  const dispatch = useDispatch();
  const { forms } = useSelector((state) => state.doc);
  const { currentUserProfile } = useSelector((state) => state.profile);
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });

  const formOptions = Array.from(
    new Map(
      (forms || [])
        .filter((form) => form.titlePermanent)
        .map((form) => {
          const titlePermanent = form.titlePermanent || form.title || form.name;
          return [
            titlePermanent,
            {
              key: titlePermanent,
              value: titlePermanent,  
              text: form.titlePermanent || form.title || form.name,
            },
          ];
        })
    ).values()
  ).sort((a, b) => a.text.localeCompare(b.text));

  const allPartyRoles = [
    "Buyer",
    "Buyer 2",
    "Buyer 3",
    "Seller",
    "Seller 2",
    "Seller 3",
    "Listing Agent",
    "Buyer Agent",
    "TC (Mine)",
    "CoAgent (Mine)",
    "Buyer Lender",
    "Escrow Officer",
    "Title Company",
    "Me (Agent)",
  ];

  const initialDefaultEmailTemplateType = getDefaultEmailTemplateType(emailTemplate);
  const initialDefaultRecipientRoles = getDefaultRecipientRoles(
    emailTemplate,
    initialDefaultEmailTemplateType
  );

  let initialValues = emailTemplate
    ? {
        defaultForSharing: false,
        defaultForSigning: false,
        defaultFormTitlePermanent: "",
        ...emailTemplate,
        defaultEmailTemplateType: initialDefaultEmailTemplateType,
        defaultRecipientRoles: initialDefaultRecipientRoles,
      }
    : {
        name: "",
        title: "",
        subject: "",
        from: "",
        message: currentUserProfile?.emailSigField || "",
        defaultForSharing: false,
        defaultForSigning: false,
        defaultEmailTemplateType: "",
        defaultFormTitlePermanent: "",
        defaultRecipientRoles: [],
      };

  const [toRoles, setToRoles] = useState(emailTemplate ? emailTemplate.to : []);
  const [ccRoles, setCcRoles] = useState(emailTemplate ? emailTemplate.cc : []);
  const [bccRoles, setBccRoles] = useState(
    emailTemplate ? emailTemplate.bcc : []
  );

  function handleToClick(role) {
    if (toRoles.includes(role)) {
      setToRoles(toRoles.filter((toRole) => toRole !== role));
    } else {
      setToRoles([...toRoles, role]);
    }
  }

  function handleCcClick(role) {
    if (ccRoles.includes(role)) {
      setCcRoles(ccRoles.filter((ccRole) => ccRole !== role));
    } else {
      setCcRoles([...ccRoles, role]);
    }
  }

  function handleBccClick(role) {
    if (bccRoles.includes(role)) {
      setBccRoles(bccRoles.filter((bccRole) => bccRole !== role));
    } else {
      setBccRoles([...bccRoles, role]);
    }
  }

  const validationSchema = Yup.object({
    name: Yup.string().required("You must provide a name for this template"),
    defaultFormTitlePermanent: Yup.string().when("defaultEmailTemplateType", {
        is: (defaultEmailTemplateType) => !!defaultEmailTemplateType,
        then: (schema) =>
          schema.required("Select the form this template should default for"),
        otherwise: (schema) => schema.notRequired(),
      }
    ),
    defaultRecipientRoles: Yup.array().when("defaultEmailTemplateType", {
      is: (defaultEmailTemplateType) => !!defaultEmailTemplateType,
      then: (schema) =>
        schema.min(1, "Select at least one role this default applies to"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  async function validateEmailTemplate(values) {
    try {
      await validationSchema.validate(values, { abortEarly: false });
      return {};
    } catch (validationError) {
      const errors = {};

      if (validationError.inner?.length) {
        validationError.inner.forEach((error) => {
          if (error.path && !errors[error.path]) {
            errors[error.path] = error.message;
          }
        });
      } else if (validationError.path) {
        errors[validationError.path] = validationError.message;
      }

      Object.values(errors).forEach((message) => toast.error(message));

      setTimeout(() => {
        if (typeof document === "undefined") return;
        const firstErrorName = Object.keys(errors)[0];
        const firstErrorElement =
          document.querySelector(
            `[data-email-template-error="${firstErrorName}"]`
          ) || document.querySelector(`[name="${firstErrorName}"]`);

        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 0);

      return errors;
    }
  }

  return (
    <ModalWrapper size="fullscreen">
      <Segment clearing>
        <div className="medium horizontal margin small top margin">
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validate={validateEmailTemplate}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                const isDefaultEmailTemplate = !!values.defaultEmailTemplateType;
                const valuesToSave = {
                  ...values,
                  defaultEmailTemplateType: isDefaultEmailTemplate
                    ? values.defaultEmailTemplateType
                    : "",
                  defaultForSharing:
                    values.defaultEmailTemplateType === "sharing",
                  defaultForSigning:
                    values.defaultEmailTemplateType === "signing",
                  defaultFormTitlePermanent:
                    isDefaultEmailTemplate
                      ? values.defaultFormTitlePermanent || ""
                      : "",
                  defaultRecipientRoles: isDefaultEmailTemplate
                    ? values.defaultRecipientRoles || []
                    : [],
                };
                emailTemplate
                  ? await updateEmailTemplateInDb(
                      valuesToSave.id,
                      valuesToSave,
                      toRoles,
                      ccRoles,
                      bccRoles
                    )
                  : await addEmailTemplateToDb(
                      valuesToSave,
                      toRoles,
                      ccRoles,
                      bccRoles
                    );
                setSubmitting(false);
                toast.success("Email template successfully updated");
                dispatch(
                  closeModal({
                    modalType: "EmailTemplatesEdit",
                  })
                );
              } catch (error) {
                toast.error(error.message);
                setSubmitting(false);
              }
            }}
          >
            {({ isSubmitting, values, setFieldValue, errors, touched, submitCount }) => (
              <Form className="ui form" autoComplete="off">
                <Header size="large" color="blue">
                  {emailTemplate
                    ? "Edit Email Template"
                    : "Create Email Template"}
                </Header>
                <Divider />
                <Grid>
                  <Grid.Row className="small vertical padding">
                    <Grid.Column width={16}>
                      <Header
                        as="h5"
                        color="blue"
                        className="mini bottom margin"
                      >
                        Template Name
                      </Header>
                      <MyTextInput
                        name="name"
                        className="small bottom margin"
                      />
                    </Grid.Column>
                    <Grid.Column width={16} className="small bottom margin">
                      <Segment className="background-lightgrey">
                        <Header
                          as="h5"
                          color="blue"
                          className="mini bottom margin"
                        >
                          Default for Form Emails
                        </Header>
                        <p className="text-muted mini bottom margin">
                          Optionally use this template as the default email for a
                          specific form.
                        </p>
                        <Grid stackable>
                          <Grid.Column width={16}>
                            <label className="bold text">Default use</label>
                            {defaultEmailTemplateTypeOptions.map((option) => (
                              <div
                                className="ui radio checkbox tiny bottom margin"
                                key={option.value || "none"}
                                style={{ display: "block" }}
                              >
                                <input
                                  type="radio"
                                  name="defaultEmailTemplateType"
                                  id={`defaultEmailTemplateType-${
                                    option.value || "none"
                                  }`}
                                  checked={
                                    values.defaultEmailTemplateType ===
                                    option.value
                                  }
                                  onChange={() => {
                                    setFieldValue(
                                      "defaultEmailTemplateType",
                                      option.value
                                    );
                                    if (!option.value) {
                                      setFieldValue(
                                        "defaultFormTitlePermanent",
                                        ""
                                      );
                                      setFieldValue("defaultRecipientRoles", []);
                                    }
                                  }}
                                />
                                <label
                                  htmlFor={`defaultEmailTemplateType-${
                                    option.value || "none"
                                  }`}
                                >
                                  {option.text}
                                </label>
                              </div>
                            ))}
                          </Grid.Column>
                          <Grid.Column width={16}>
                            <MySelectInput
                              name="defaultFormTitlePermanent"
                              label="Associated form"
                              placeholder="Select form"
                              options={formOptions}
                              disabled={!values.defaultEmailTemplateType}
                              clearable
                              onChange={(_e, data) =>
                                setFieldValue(
                                  "defaultFormTitlePermanent",
                                  data.value || ""
                                )
                              }
                            />
                          </Grid.Column>
                          <Grid.Column width={16}>
                            <label className="bold text">
                              Who this default applies to
                            </label>
                            <Grid stackable className="zero top margin">
                              {defaultRecipientRoleOptions.map((option) => (
                                <Grid.Column
                                  mobile={16}
                                  tablet={8}
                                  computer={2}
                                  key={option.value}
                                >
                                  <MyCheckbox
                                    name="defaultRecipientRoles"
                                    value={option.value}
                                    label={option.text}
                                    disabled={!values.defaultEmailTemplateType}
                                  />
                                </Grid.Column>
                              ))}
                            </Grid>
                            {(touched.defaultRecipientRoles || submitCount > 0) &&
                              errors.defaultRecipientRoles && (
                                <Label
                                  basic
                                  size="large"
                                  color="red"
                                  data-email-template-error="defaultRecipientRoles"
                                >
                                  {errors.defaultRecipientRoles}
                                </Label>
                              )}
                          </Grid.Column>
                        </Grid>
                      </Segment>
                    </Grid.Column>
                    <Grid.Column width={16} className="small bottom margin">
                      <Header
                        as="h5"
                        color="blue"
                        className="small bottom margin"
                      >
                        Recipients
                      </Header>
                      <div className="mini bottom margin">
                        <b>To:</b>{" "}
                        {allPartyRoles.map((role) => (
                          <Label
                            key={role}
                            style={{
                              cursor: "pointer",
                              marginBottom: "2px",
                            }}
                            color={toRoles.includes(role) ? "blue" : null}
                            onClick={() => handleToClick(role)}
                          >
                            {role}
                          </Label>
                        ))}
                      </div>
                      <div className="mini bottom margin">
                        <b>cc:</b>{" "}
                        {allPartyRoles.map((role) => (
                          <Label
                            key={role}
                            style={{
                              cursor: "pointer",
                              marginBottom: "2px",
                            }}
                            color={ccRoles.includes(role) ? "blue" : null}
                            onClick={() => handleCcClick(role)}
                          >
                            {role}
                          </Label>
                        ))}
                      </div>
                      <div className="mini bottom margin">
                        <b>bcc:</b>{" "}
                        {allPartyRoles.map((role) => (
                          <Label
                            key={role}
                            style={{
                              cursor: "pointer",
                              marginBottom: "2px",
                            }}
                            color={bccRoles.includes(role) ? "blue" : null}
                            onClick={() => handleBccClick(role)}
                          >
                            {role}
                          </Label>
                        ))}
                      </div>
                    </Grid.Column>
                    <Grid.Column width={16}>
                      <Header
                        as="h5"
                        color="blue"
                        className="mini bottom margin"
                      >
                        Subject
                      </Header>
                      <MyTextInput
                        name="subject"
                        className="small bottom margin"
                      />
                    </Grid.Column>
                    <Grid.Column width={16}>
                      <Header
                        as="h5"
                        color="blue"
                        className="mini bottom margin"
                      >
                        Message
                      </Header>
                      <MyTextArea
                        name="message"
                        className="small bottom margin"
                        rows="12"
                      />
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
                <Divider className="medium top margin" />
                <Button
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  type="submit"
                  floated={isMobile ? null : "right"}
                  primary
                  content="Submit"
                  className={isMobile ? "fluid medium bottom margin" : null}
                />
                <Button
                  disabled={isSubmitting}
                  onClick={() =>
                    dispatch(
                      closeModal({
                        modalType: "emailTemplatesEdit",
                      })
                    )
                  }
                  to="#"
                  type="button"
                  floated={isMobile ? null : "right"}
                  content="Cancel"
                  className={isMobile ? "fluid medium bottom margin" : null}
                />
              </Form>
            )}
          </Formik>
        </div>
      </Segment>
    </ModalWrapper>
  );
}
